import React, { useCallback, useRef, useState } from 'react';
import { useMediaUpload } from '../../../shared/hooks/useMediaUpload';
import { useProductMedia, type MediaTranscodeState } from '../../../shared/hooks/useProductMedia';
import type { ProductMediaItem } from '../../../pages/AddProductPage/types';

// PE-1283 §7 E4 — product-level media gallery (images + video).
//
// Each entry is the serialized shape the BFF persists (media[] on product
// create/update): { url, type, posterUrl?, sortOrder, status }. Images upload via
// the existing proxied path; video via the signed direct-to-GCS + transcode flow.

export type { ProductMediaItem };

interface ProductMediaGalleryProps {
  items: ProductMediaItem[];
  onChange: (items: ProductMediaItem[]) => void;
  maxItems?: number;
  solutionId?: string;
}

let counter = 0;
const nextLocalId = () => `m_${Date.now()}_${counter++}`;

export function ProductMediaGallery({
  items,
  onChange,
  maxItems = 8,
  solutionId = 'rack-and-box',
}: ProductMediaGalleryProps) {
  const { upload: uploadImage } = useMediaUpload();
  const { uploadVideo } = useProductMedia();
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep a live ref to items so concurrent uploads don't clobber each other.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const patch = useCallback(
    (localId: string, p: Partial<ProductMediaItem>) => {
      const updated = itemsRef.current.map((it) => (it.localId === localId ? { ...it, ...p } : it));
      itemsRef.current = updated;
      onChange(updated);
    },
    [onChange],
  );

  const removeByLocalId = useCallback(
    (localId: string) => {
      const updated = itemsRef.current
        .filter((it) => it.localId !== localId)
        .map((it, i) => ({ ...it, sortOrder: i }));
      itemsRef.current = updated;
      onChange(updated);
    },
    [onChange],
  );

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files) return;
      const remaining = maxItems - itemsRef.current.length;
      const toProcess = Array.from(files).slice(0, Math.max(0, remaining));

      for (const file of toProcess) {
        const localId = nextLocalId();
        const previewUrl = URL.createObjectURL(file);
        const isVideo = file.type.startsWith('video/');

        const placeholder: ProductMediaItem = {
          localId,
          mediaId: null,
          url: previewUrl,
          type: isVideo ? 'VIDEO' : 'IMAGE',
          sortOrder: itemsRef.current.length,
          status: 'PROCESSING',
          _previewUrl: previewUrl,
        };
        const withPlaceholder = [...itemsRef.current, placeholder];
        itemsRef.current = withPlaceholder;
        onChange(withPlaceholder);

        if (isVideo) {
          try {
            const result = await uploadVideo(file, solutionId, (state: MediaTranscodeState) => {
              patch(localId, { status: state === 'failed' ? 'FAILED' : 'PROCESSING' });
            });
            patch(localId, {
              mediaId: result.mediaId,
              url: result.url,
              posterUrl: result.posterUrl,
              status: result.processing ? 'PROCESSING' : 'READY',
              _previewUrl: undefined,
            });
          } catch {
            patch(localId, { status: 'FAILED' });
          }
        } else {
          try {
            const uploaded = await uploadImage(file, solutionId);
            patch(localId, {
              mediaId: uploaded.id,
              url: uploaded.url,
              status: 'READY',
              _previewUrl: undefined,
            });
          } catch {
            removeByLocalId(localId);
          }
        }
      }
    },
    [maxItems, onChange, patch, removeByLocalId, solutionId, uploadImage, uploadVideo],
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const canAdd = items.length < maxItems;

  return (
    <div className="space-y-3">
      <div>
        <label className="text-[14px] font-[600] text-foreground block mb-1">Product media</label>
        <p className="text-[12px] text-muted-foreground">
          Add product images and a video. Video is transcoded automatically — you can publish while it
          finishes processing.
        </p>
      </div>

      {canAdd && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-[12px] p-6 cursor-pointer text-center transition-colors ${
            dragging ? 'border-[#3F3E42] bg-muted' : 'border-[#D9D9D9] hover:border-[#9B97A1]'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-muted mx-auto mb-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                stroke="#7C797C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                points="17 8 12 3 7 8"
                stroke="#7C797C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line x1="12" y1="3" x2="12" y2="15" stroke="#7C797C" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <p className="text-[13px] text-muted-foreground">
            <span className="font-[600] text-foreground">Click to upload</span> or drag and drop
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Images or video · up to {maxItems} item{maxItems > 1 ? 's' : ''}
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {items.map((item) => (
            <div
              key={item.localId}
              className="relative border border-[#E5E7EB] rounded-[10px] overflow-hidden bg-card"
            >
              <div className="aspect-square bg-muted flex items-center justify-center">
                {item.type === 'VIDEO' ? (
                  <video
                    src={item._previewUrl ?? item.url}
                    poster={item.posterUrl}
                    className="w-full h-full object-contain bg-black"
                    muted
                    playsInline
                    controls={!item._previewUrl && item.status === 'READY'}
                  />
                ) : (
                  <img
                    src={item._previewUrl ?? item.url}
                    alt="Product media"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              <div className="absolute top-1 left-1 flex gap-1">
                <span className="text-[10px] font-[600] px-1.5 py-0.5 rounded bg-black/60 text-white">
                  {item.type === 'VIDEO' ? 'Video' : 'Image'}
                </span>
                {item.type === 'VIDEO' && item.status === 'PROCESSING' && (
                  <span className="text-[10px] font-[600] px-1.5 py-0.5 rounded bg-amber-500 text-white">
                    Processing…
                  </span>
                )}
                {item.status === 'FAILED' && (
                  <span className="text-[10px] font-[600] px-1.5 py-0.5 rounded bg-red-500 text-white">
                    Failed
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={() => removeByLocalId(item.localId)}
                aria-label="Remove media"
                className="absolute top-1 right-1 p-1 rounded-[6px] bg-card/80 text-muted-foreground hover:bg-[#FECDCD] hover:text-red-500 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M2 4h12M5.333 4V2.667h5.334V4M6.667 7.333v4M9.333 7.333v4M3.333 4l.667 9.333h8L12.667 4"
                    stroke="currentColor"
                    strokeWidth="1.33"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
